import {
  Card,
  CardBody,
  CardHeader,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerContentProps,
  DrawerFooter,
  DrawerHeader,
  SimpleGrid,
  Stack,
  Text,
} from '@chakra-ui/react';
import { useFetchAfeDetailsQuery } from 'api';
import { PostInvoiceRequest } from 'api/bindings/PostInvoiceRequest';
import { PostInvoiceTransactionRequest } from 'api/bindings/PostInvoiceTransactionRequest';
import { PutInvoiceRequest } from 'api/bindings/PutInvoiceRequest';
import Decimal from 'decimal.js';
import { CurrencyLabel, InvoiceWorkflowStage } from 'features';
import { FormButtonGroup } from 'features/components';
import { useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

export type InvoiceModel = PostInvoiceRequest | PutInvoiceRequest;

export function isPostInvoiceRequest(
  dto: InvoiceModel
): dto is PostInvoiceRequest {
  return 'afeId' in dto;
}
export function isPutInvoiceRequest(
  dto: InvoiceModel
): dto is PutInvoiceRequest {
  return !('afeId' in dto);
}
export interface InvoiceFormReviewDrawerContentProps
  extends Omit<DrawerContentProps, 'onSubmit'> {
  afeName: string;
  initialStage: InvoiceWorkflowStage;
  header: InvoiceModel;
  revision: PostInvoiceTransactionRequest | undefined;
  onSubmit: (form: Form) => void;
  onClose: () => void;
}

interface Form {
  desiredNextStage: InvoiceWorkflowStage | undefined;
}

export const InvoiceFormReviewDrawerContent = (
  props: InvoiceFormReviewDrawerContentProps
) => {
  const {
    afeName,
    header: invoice,
    initialStage,
    revision,
    onSubmit,
    onClose,
    ...rest
  } = props;

  // const { data: afe } = useFetchAfeDetailsQuery(invoice.afeId);

  const form = useForm<Form>({
    mode: 'all',
    defaultValues: {
      desiredNextStage: undefined,
    },
  });
  const { formState, handleSubmit } = form;

  const revisionDetails = useMemo(() => {
    return {
      total: revision?.accounts.reduce(
        (sum, n) => sum.add(new Decimal(n.amount)),
        new Decimal(0)
      ),
    };
  }, [revision]);

  return (
    <DrawerContent {...rest}>
      <FormProvider {...form}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <DrawerCloseButton />
          <DrawerHeader>Review Submission</DrawerHeader>
          <DrawerBody>
            <Stack spacing={4}>
              {initialStage === InvoiceWorkflowStage.invoice && invoice && (
                <Card>
                  <CardHeader>
                    <Text as="b">Invoice</Text>
                  </CardHeader>
                  <CardBody>
                    <SimpleGrid columns={2} gap={2}>
                      <Text as="b">Name:</Text>
                      <Text>{invoice.name}</Text>
                      <Text as="b">AFE:</Text>
                      <Text>{afeName}</Text>
                    </SimpleGrid>
                  </CardBody>
                </Card>
              )}
              {revision && (
                <Card>
                  <CardHeader>
                    <Text as="b">New Revision</Text>
                  </CardHeader>
                  <CardBody>
                    <SimpleGrid columns={2} gap={2}>
                      <Text as="b">Name:</Text>
                      <Text>{revision.name}</Text>
                      <Text as="b">Total:</Text>
                      <CurrencyLabel value={revisionDetails.total} />
                    </SimpleGrid>
                  </CardBody>
                </Card>
              )}
            </Stack>
          </DrawerBody>
          <DrawerFooter>
            <FormButtonGroup
              disabledWhenClean={false}
              onCancel={onClose}
              onConfirm={handleSubmit(onSubmit)}
            />
          </DrawerFooter>
        </form>
      </FormProvider>
    </DrawerContent>
  );
};
